// Extra Effy Order feature landing pages, one for each search phrase shop owners type
// (GST report, stock management, sales report, invoice billing, small business billing,
// sales management). Same entry shape as EFFY_ORDER_PAGES in seoPages.js, plus a `guide`
// list (heading + paragraphs) shown as readable text under the hero.
//
// Every page here must also be listed in scripts/prerender.js, vercel.json and
// public/sitemap.xml (see the header of seoPages.js).
// Only describe things the app really does. Report names and filters below match the
// Reports section of the app (Sales, GST, Inventory, Closing and Category-wise reports).

export const KEYWORD_PAGES = [
  {
    path: '/products/effy-order/gst-report-software',
    navLabel: 'GST Report',
    badge: 'GST Report Software',
    metaTitle: 'GST Report Software for Small Business | Effy Order',
    metaDescription: 'GST report software that turns invoices into a tax summary. Pick dates and order status, download the report and give your accountant clean figures.',
    heading: 'GST report software that',
    headingHighlight: 'saves your month-end.',
    subtitle: 'Every invoice you create already holds the tax details. Effy Order gathers them into a GST report for the dates you choose, so you stop copying numbers from bills into a sheet.',
    heroPoints: ['GST summary with tax breakdown', 'Filter by date and status', 'CGST, SGST and IGST', 'HSN-wise tax profile', 'Credit and debit notes', 'Download for your accountant'],
    image: '/images/GSTReports.PNG',
    imageAlt: 'GST report screen in Effy Order with date range and status filters',
    guide: [
      {
        title: 'Why a GST report takes so long by hand',
        paragraphs: [
          'At the end of every month a shop owner or an accountant has to answer one question: how much tax did we collect, and on which sales. When bills are written by hand or typed into a spreadsheet, the answer comes from adding up hundreds of small numbers. One missed bill or one wrong rate and the totals stop matching the books.',
          'The fix is to let the billing software do the adding. Because Effy Order calculates the tax on the invoice itself, from the rate saved for each HSN code, the same figures can be collected again later without anyone retyping them.',
        ],
      },
      {
        title: 'What the GST report gives you',
        paragraphs: [
          'The GST report is a summary of your orders with the tax broken down. You choose a start date and an end date, and you can narrow the list by order status or payment status. The report is generated and downloaded to your computer, ready to open and share with your accountant.',
          'Credit notes and debit notes are recorded against your orders, so returns and corrections are part of the same records. The report is a working summary. Your accountant will still decide how the figures are used when returns are filed, and the exact format needed can change, so confirm it with them.',
        ],
      },
    ],
    sections: [
      {
        label: 'Tax on every invoice',
        title: 'The tax is calculated once, at billing time',
        description: 'Set the GST rate for each HSN code in your tax profile. Each invoice picks it up, so the report is only a summary of figures that already exist.',
        points: [
          'CGST and SGST for sales inside your state.',
          'IGST for sales to customers in other states.',
          'Tax is worked out after item-wise and overall discounts.',
          'Additional charges such as delivery can carry their own tax.',
        ],
      },
      {
        label: 'The report',
        title: 'Choose the dates, download the summary',
        description: 'Pick the period you want to look at, add filters if you need them and download. No export wizard, no manual sheet.',
        points: [
          'Choose a from date and a to date, up to 90 days at a time.',
          'Filter by order status such as Initiated, In Progress or Completed.',
          'Filter by payment status: Pending, Partially Paid or Paid.',
          'The file downloads to your device so you can forward it.',
        ],
      },
      {
        label: 'Related records',
        title: 'Returns and corrections stay linked',
        description: 'A return after the invoice is common in retail. The correction is saved against the order, not in a separate notebook.',
        points: [
          'Credit notes for returned or overcharged items.',
          'Debit notes for goods returned to suppliers.',
          'Sales reports and inventory reports alongside the GST report.',
          'Customer and supplier history for any query from your accountant.',
        ],
      },
    ],
    faqs: [
      { question: 'What is GST report software?', answer: 'GST report software collects the tax details from your invoices and shows them as a summary for a period. In Effy Order the report is built from the orders you have already billed, so you do not retype anything.' },
      { question: 'Which dates can I use for the GST report?', answer: 'You choose a from date and a to date. The range is limited to 90 days at a time, so for a longer period you download it in parts.' },
      { question: 'Can I filter the GST report by order or payment status?', answer: 'Yes. You can narrow the report by order status and by payment status before generating it.' },
      { question: 'Does the report split CGST, SGST and IGST?', answer: 'Tax on each invoice is calculated as CGST and SGST for sales inside your state and IGST for sales to other states. The report gives a tax breakdown of your orders for the period you choose.' },
      { question: 'Can I use the report to file my GST return directly?', answer: 'The report is a summary you can give to your accountant. Rules and return formats change, so confirm with your accountant what they need before you file.' },
      { question: 'Where do I download the GST report?', answer: 'Open the Reports section of Effy Order, choose the GST report, set the dates and download the file to your phone, tablet or computer.' },
    ],
    related: ['/products/effy-order/gst-billing-software', '/products/effy-order/sales-report-software', '/gst-billing-software-india', '/how-to-file-gstr-1-from-sales-report'],
  },
  {
    path: '/products/effy-order/stock-management-software',
    navLabel: 'Stock Management',
    badge: 'Stock Management Software',
    metaTitle: 'Stock Management Software for Shops | Effy Order',
    metaDescription: 'Stock management software for shops. See stock levels, stock value, daily movement and expiring items, and get low stock reports before you run out.',
    heading: 'Stock management software that',
    headingHighlight: 'shows where every item stands.',
    subtitle: 'Know what is on the shelf, what it is worth, what moved today and what is about to expire. Effy Order keeps the count and gives you the reports.',
    heroPoints: ['Stock levels and valuation', 'Low stock report', 'Daily stock movement', 'Expiry report', 'Bulk stock update from Excel', 'Barcode scanning'],
    image: '/images/Inventory1.PNG',
    imageAlt: 'Stock list with quantities in Effy Order',
    guide: [
      {
        title: 'Stock reports for the questions you really ask',
        paragraphs: [
          'Owners ask the same few questions about stock again and again. What do I have right now? What is it worth? What do I need to reorder? What came in and went out yesterday? Is anything about to expire? Effy Order has a report for each of these in the Reports section.',
          'The Inventory Report offers a full inventory report, a low stock report, stock availability, stock valuation, a daily stock movement report, an expiry report and an item-wise sales and profit report. You pick the type, apply the filters it offers and download the file.',
        ],
      },
      {
        title: 'Why counts go wrong, and how this keeps them right',
        paragraphs: [
          'Most stock errors come from forgetting to write something down: an item sold at the counter, a return put back on the shelf, a delivery that was never entered. When stock is only updated by hand, the number slowly drifts away from the truth.',
          'In Effy Order the movements happen where the work happens. Creating an order takes the quantity out, receiving a purchase order adds it, and a credit note for a return puts it back. For services or items you do not want to count, stock updates can be switched off for that item.',
        ],
      },
    ],
    sections: [
      {
        label: 'Inventory reports',
        title: 'Seven stock reports in one place',
        description: 'Choose the report that answers your question and download it as a file you can sort, share or print.',
        points: [
          'Full inventory report and stock availability.',
          'Stock valuation to see what your stock is worth.',
          'Low stock report with a threshold you set.',
          'Daily stock movement with opening stock, stock in, stock out and closing stock.',
        ],
      },
      {
        label: 'Expiry and batches',
        title: 'Catch items before they expire',
        description: 'For shops that sell products with an expiry date, the expiry report lists what has expired and what is about to.',
        points: [
          'Expiry report for the next week, the next month or a number of days you choose.',
          'Includes items that have already expired, and only items that still have stock.',
          'Stale stock insights show products that are not selling.',
          'Item-wise sales and profit report for the dates you choose.',
        ],
      },
      {
        label: 'Daily work',
        title: 'Update stock quickly',
        description: 'Large catalogues do not have to be edited one item at a time.',
        points: [
          'Bulk upload items and bulk set stock from Excel.',
          'Express quantity update by scanning a barcode.',
          'Generate and print barcode labels for loose items.',
          'Search any item by name, SKU or barcode.',
        ],
      },
    ],
    faqs: [
      { question: 'What is stock management software?', answer: 'Stock management software records how much of every product you hold and updates the count when you sell, buy or take returns. Effy Order also gives reports on stock levels, value and movement.' },
      { question: 'Can I see the value of my stock?', answer: 'Yes. The Inventory Report includes a stock valuation report so you can see what your stock is worth.' },
      { question: 'Can I see what moved in and out each day?', answer: 'Yes. The daily stock movement report shows opening stock, stock in, stock out and closing stock for each item on days that had activity. It is available for up to 7 days per download.' },
      { question: 'Does Effy Order report items that are about to expire?', answer: 'Yes. The expiry report lists items that have expired and items expiring within a week, a month or a number of days you choose. Only items with stock are listed.' },
      { question: 'Can I stop stock counting for some items?', answer: 'Yes. Stock updates can be switched off for an item, which is useful for services such as a haircut, where there is nothing to count.' },
      { question: 'Can I bring in my stock list from Excel?', answer: 'Yes. You can bulk upload items and bulk update or set stock from an Excel sheet.' },
    ],
    related: ['/products/effy-order/inventory-management-software', '/products/effy-order/barcode-billing-software', '/inventory-management-for-small-business', '/stock-management-excel-vs-software'],
  },
  {
    path: '/products/effy-order/sales-report-software',
    navLabel: 'Sales Report',
    badge: 'Sales Report Software',
    metaTitle: 'Sales Report Software for Shops and Traders | Effy Order',
    metaDescription: 'Sales report software with day-end closing, category-wise sales and a dashboard. See sales, profit, money collected and pending amounts for any period.',
    heading: 'Sales report software that',
    headingHighlight: 'shows profit, not just totals.',
    subtitle: 'From the sales report to the day-end closing report, see how much you sold, how much you collected, what is still pending and what you earned.',
    heroPoints: ['Sales report with filters', 'Day-end closing report', 'Category-wise sales', 'Profit and pending amounts', 'Top products and buyers', 'Excel and CSV downloads'],
    image: '/images/dashboard.PNG',
    imageAlt: 'Sales dashboard with daily and monthly charts in Effy Order',
    guide: [
      {
        title: 'Four reports, four different questions',
        paragraphs: [
          'The Sales Report answers what was sold in a period. You choose today, yesterday, the last 7 days, the last 30 days or a custom range of up to 90 days, and you can filter by order status and payment status before downloading it as a CSV file.',
          'The Closing Report answers how the day went. It is a day-end summary of bills, money collected by payment mode, pending amount and profit for up to 7 days, downloaded as an Excel file with a sales closing sheet and a stock movement sheet.',
          'The Category-wise Sales Report answers which department earns the most. It gives the number of orders, total sales, total profit and total pending amount for each category, for up to 30 days. The Supplier Details Report shows sales and profit out of each supplier’s stock.',
        ],
      },
      {
        title: 'Look at the numbers in the way you work',
        paragraphs: [
          'Some owners want a spreadsheet to sort and filter. Others only want a picture. Effy Order has both: downloadable reports for detail, and a dashboard with daily and monthly sales charts, top products and top buyers for the quick look in the morning.',
          'If you track which team member made each sale, the sales can also be looked at employee by employee on the plans that include employee tracking.',
        ],
      },
    ],
    sections: [
      {
        label: 'Sales report',
        title: 'Filter first, then download',
        description: 'Ask for exactly the orders you want, so you are not cleaning a huge sheet afterwards.',
        points: [
          'Today, yesterday, last 7 days, last 30 days or a custom range.',
          'Filter by order status and payment status.',
          'Customer-wise sales to find your best buyers.',
          'Downloads as a file you can open in Excel.',
        ],
      },
      {
        label: 'Closing report',
        title: 'Close the day with the real numbers',
        description: 'At the end of the day you want one summary, not a pile of bills.',
        points: [
          'Number of bills, with rejected or cancelled bills counted separately.',
          'Money collected, split by payment mode.',
          'Pending amount and profit for the period.',
          'Day-wise figures and a bill list in the Excel file.',
        ],
      },
      {
        label: 'Insights',
        title: 'See what sells and who buys',
        description: 'Use the dashboard and the category report to decide what to stock more of.',
        points: [
          'Monthly and daily order trends.',
          'Top products and top buyers.',
          'Sales, profit and pending amount by category.',
          'Item-wise sales and profit from the Inventory Report.',
        ],
      },
    ],
    faqs: [
      { question: 'What is sales report software?', answer: 'Sales report software collects the bills you have created and summarises them: what was sold, to whom, how much was received and how much is pending. Effy Order does this from the orders you already enter.' },
      { question: 'What periods can I choose for the sales report?', answer: 'You can choose today, yesterday, the last 7 days, the last 30 days or a custom range of up to 90 days.' },
      { question: 'What does the closing report contain?', answer: 'It is a day-end summary of bills, money collected by payment mode, pending amount and profit. It covers up to 7 days and downloads as an Excel file.' },
      { question: 'Can I see profit in the reports?', answer: 'Yes. The closing report, the category-wise sales report and the item-wise sales and profit report all show profit.' },
      { question: 'Can I see which category sells the most?', answer: 'Yes. The category-wise sales report shows orders, sales, profit and pending amount for each category for up to 30 days.' },
      { question: 'Can I check sales on my phone?', answer: 'Yes. The dashboard and reports open in any browser on a phone, tablet or computer.' },
    ],
    related: ['/products/effy-order/sales-management-software', '/products/effy-order/gst-report-software', '/products/effy-order/order-management-software', '/how-to-file-gstr-1-from-sales-report'],
  },
  {
    path: '/products/effy-order/invoice-billing-software',
    navLabel: 'Invoice Billing',
    badge: 'Invoice Billing Software',
    metaTitle: 'Invoice Billing Software for Small Business | Effy Order',
    metaDescription: 'Invoice billing software to create invoices in seconds, print on A4 or thermal paper, share on WhatsApp and track payments and dues for every customer.',
    heading: 'Invoice billing software that',
    headingHighlight: 'gets you paid on time.',
    subtitle: 'Create an invoice, print or share it, record what the customer paid and see what is still due. All from one screen, on a phone, tablet or computer.',
    heroPoints: ['Invoices in seconds', 'A4 and thermal receipt print', 'Share by WhatsApp and email', 'Payments and pending dues', 'Credit and debit notes', 'Customer history'],
    image: '/images/OrderUI.PNG',
    imageAlt: 'Order and invoice list in Effy Order',
    guide: [
      {
        title: 'An invoice is more than a printout',
        paragraphs: [
          'A good invoice does three jobs. It tells the customer what they bought and what they owe. It gives you a record to look up later when there is a question. And it feeds your stock and your reports. A paper bill does only the first.',
          'In Effy Order the invoice is created from an order, so the same entry updates your stock, your customer record and your reports. You choose the billing mode that fits your counter: the full form, a quick bill, or the express mode made for phones and tablets.',
        ],
      },
      {
        title: 'From invoice to payment',
        paragraphs: [
          'Many small businesses sell part on credit. An invoice that records how much was paid and how much is left makes it easy to follow up. Payment status moves through Pending, Partially Paid and Paid, and the pending amount is shown for each customer.',
          'When a customer pays later you record a payment and print a receipt. If the customer has paid in advance, the advance can be adjusted against a new order. Returns are handled with credit notes, which keep the original invoice untouched.',
        ],
      },
    ],
    sections: [
      {
        label: 'Create',
        title: 'Three billing modes, one result',
        description: 'Use the speed that suits the moment. The invoice and the stock update are the same either way.',
        points: [
          'Full order form with all the details.',
          'Quick bill for fast counter sales.',
          'Express mode for touch screens, with barcode scanning.',
          'Item-wise and overall discounts, flat or percentage.',
        ],
      },
      {
        label: 'Print and share',
        title: 'Give the customer a copy in any form',
        description: 'Print on paper or send the details so the customer has the bill on their phone.',
        points: [
          'Print-ready invoice layout.',
          'Thermal receipt layout for roll printers.',
          'Share invoice details by WhatsApp and email.',
          'Warranty details printed against each item.',
        ],
      },
      {
        label: 'Collect',
        title: 'Know who owes you money',
        description: 'The invoice carries its payment record, so you do not need a separate notebook for dues.',
        points: [
          'Amount received and payment mode saved on the order.',
          'Payment-in receipts for later payments.',
          'Customer advance adjusted on new orders.',
          'Pending balance per customer.',
        ],
      },
    ],
    faqs: [
      { question: 'What is invoice billing software?', answer: 'Invoice billing software creates bills for your customers, keeps a record of each one and tracks what has been paid. Effy Order also updates your stock and reports from every invoice.' },
      { question: 'Can I print invoices on a thermal receipt printer?', answer: 'Yes. Effy Order has a thermal receipt layout for roll printers as well as a normal print layout. Set the printer paper size and turn off browser headers and footers for the cleanest result.' },
      { question: 'Can I send an invoice to a customer on WhatsApp?', answer: 'Yes. You can share invoice details with customers through WhatsApp and email.' },
      { question: 'Can I record a part payment on an invoice?', answer: 'Yes. Record the amount received and the payment mode. The payment status shows Partially Paid and the balance stays pending until the customer pays.' },
      { question: 'What happens if goods are returned?', answer: 'Create a credit note. It is linked to the order and puts the items back into stock, and the original invoice stays as it was.' },
      { question: 'Can I add a discount to an invoice?', answer: 'Yes. You can give item-wise discounts and an overall discount, as a flat amount or a percentage.' },
    ],
    related: ['/products/effy-order/gst-billing-software', '/products/effy-order/billing-software-for-small-business', '/gst-bill-format-for-retail-shop', '/products/effy-order/delivery-challan-software'],
  },
  {
    path: '/products/effy-order/billing-software-for-small-business',
    navLabel: 'Small Business Billing',
    badge: 'Billing Software for Small Business',
    metaTitle: 'Billing Software for Small Business in India | Effy Order',
    metaDescription: 'Billing software for small business owners in India. Bill customers, keep stock right and track dues in one browser app. Try it free for 30 days.',
    heading: 'Billing software for small business',
    headingHighlight: 'that does not need training.',
    subtitle: 'One simple app for the whole counter: bill customers, update stock, track who owes you and see how the day went. It runs in the browser, so there is nothing to install.',
    heroPoints: ['Runs in the browser', 'Bills, stock and dues together', 'English and Tamil', 'Barcode scanning', 'Reports for your accountant', '30-day free trial'],
    image: '/images/Billing.PNG',
    imageAlt: 'Billing screen of Effy Order on a small business counter',
    guide: [
      {
        title: 'What a small business needs from billing software',
        paragraphs: [
          'A small business does not need a hundred features. It needs billing to be quick when a queue is waiting, stock that is right when a customer asks for an item, and a clear record of who has paid. Anything that slows the counter down will be dropped within a week, however good it looks.',
          'Effy Order is built around these everyday jobs. The counter staff scan or search an item, enter what the customer paid and print the bill. The stock, the customer record and the reports update from that one entry.',
        ],
      },
      {
        title: 'Starting without a big setup',
        paragraphs: [
          'You open the app in a browser, so there is no installation and no special machine. Items and stock can be added in bulk from an Excel sheet instead of typing each product, and past orders can be uploaded from Excel as well.',
          'The app can be used in English or Tamil, which helps when staff are more comfortable in Tamil. Plans start at ₹199 per month, and the plans that include a free trial give you 30 days to check whether it fits your counter before you pay.',
        ],
      },
    ],
    sections: [
      {
        label: 'At the counter',
        title: 'Bill fast, even at rush hour',
        description: 'Choose the billing mode that suits your counter and your device.',
        points: [
          'Scan barcodes or search items by name.',
          'Express mode designed for phones and tablets.',
          'POS billing look for a supermarket-style checkout.',
          'GST invoices with CGST, SGST and IGST.',
        ],
      },
      {
        label: 'Behind the counter',
        title: 'Stock, customers and suppliers in sync',
        description: 'Each bill updates the rest of your records so you do not enter anything twice.',
        points: [
          'Stock goes down on every sale and up on every purchase.',
          'Customers are saved the first time you bill them.',
          'Purchase orders and supplier payments.',
          'Pending dues for customers and suppliers.',
        ],
      },
      {
        label: 'At month end',
        title: 'Reports without extra work',
        description: 'Everything you need to look at your business, or to send to your accountant, comes from the bills you already made.',
        points: [
          'Sales report, GST report and inventory reports.',
          'Closing report for the day.',
          'Dashboard with top products and buyers.',
          'Export reports as files for your accountant.',
        ],
      },
    ],
    faqs: [
      { question: 'Which billing software is good for a small business in India?', answer: 'Look for software that bills quickly, calculates GST correctly, keeps stock in sync and works on the devices you already have. Effy Order does these in a browser, with plans from ₹199 per month.' },
      { question: 'Do I need to install anything?', answer: 'No. Effy Order opens in a web browser on a desktop, tablet or phone.' },
      { question: 'Can my staff use it in Tamil?', answer: 'Yes. The app can be used in English or Tamil.' },
      { question: 'Is there a free trial?', answer: 'Yes. Plans that offer it start with a 30-day free trial. See the pricing page for the plan details.' },
      { question: 'Can I move my item list from Excel?', answer: 'Yes. Items and stock quantities can be uploaded in bulk from an Excel sheet.' },
      { question: 'Will it work for a shop with more than one staff member?', answer: 'Yes. Several people can bill, and plans that include employee tracking let you record who made each sale.' },
    ],
    related: ['/products/effy-order/invoice-billing-software', '/products/effy-order/gst-billing-software', '/products/effy-order/barcode-billing-software', '/billing-software-for-tamil-nadu-shops'],
  },
  {
    path: '/products/effy-order/sales-management-software',
    navLabel: 'Sales Management',
    badge: 'Sales Management Software',
    metaTitle: 'Sales Management Software for Small Business | Effy Order',
    metaDescription: 'Sales management software to run quotations, orders, invoices and payments in one flow. See top products and buyers and follow up pending dues.',
    heading: 'Sales management software that',
    headingHighlight: 'follows every sale to payment.',
    subtitle: 'From the first quotation to the last payment, keep each sale in one place and know what is closed, what is open and what is still unpaid.',
    heroPoints: ['Quotation to order to invoice', 'Order and payment status', 'Customer history and dues', 'Dashboard and top buyers', 'Employee-wise sales', 'WhatsApp and email updates'],
    image: '/images/Customer.PNG',
    imageAlt: 'Customer list with order history in Effy Order',
    guide: [
      {
        title: 'A sale does not end at the invoice',
        paragraphs: [
          'Selling is a chain: an enquiry, a price, an order, a delivery, an invoice and finally the money. When each step lives in a different notebook or chat, things fall through the gaps. A quote is forgotten, a delivery is not billed or a payment is never chased.',
          'Effy Order keeps the whole chain on one record. A quotation can be turned into an order, the order carries its status and its payment status, and the invoice, the delivery challan and the receipts are all made from it.',
        ],
      },
      {
        title: 'Know your customers and your dues',
        paragraphs: [
          'Each customer is saved when you first bill them, with every order they have placed and the balance they still owe. Before you accept a big order on credit you can see their history. When you follow up, you know the exact amount.',
          'The dashboard adds the bigger picture: daily and monthly order trends, top products and top buyers. If you record who made each sale, the plans with employee tracking let you compare sales by team member.',
        ],
      },
    ],
    sections: [
      {
        label: 'Pipeline',
        title: 'Quotation, order, delivery, invoice',
        description: 'Each stage reuses what was entered in the stage before, so nothing is typed twice.',
        points: [
          'Quotations that convert into orders.',
          'Order status: Initiated, In Progress, Completed.',
          'Delivery challans printed from the order.',
          'Invoices and estimates from the same order.',
        ],
      },
      {
        label: 'Collections',
        title: 'Chase payments with the right numbers',
        description: 'Payment status and pending amounts are always visible, per order and per customer.',
        points: [
          'Payment status: Pending, Partially Paid, Paid.',
          'Payment-in receipts for later payments.',
          'Customer advance adjustment.',
          'Pending balance per customer.',
        ],
      },
      {
        label: 'Insight',
        title: 'See who and what drives sales',
        description: 'Use the numbers to decide where to spend your time.',
        points: [
          'Top products and top buyers on the dashboard.',
          'Customer-wise sales reports.',
          'Optional notification by WhatsApp and email.',
          'Sold-by tracking where employee tracking is enabled.',
        ],
      },
    ],
    faqs: [
      { question: 'What is sales management software?', answer: 'Sales management software helps you follow each sale from quotation and order to invoice and payment, and shows customer history, dues and sales trends.' },
      { question: 'Can I turn a quotation into an order?', answer: 'Yes. An approved quotation can be converted into an order with the items, prices and discounts already filled in.' },
      { question: 'How do I see which customers have not paid?', answer: 'Each order has a payment status and each customer shows a pending balance, so you can list who owes money and how much.' },
      { question: 'Can I see my best customers and products?', answer: 'Yes. The dashboard shows top products and top buyers, and customer-wise sales reports are available.' },
      { question: 'Can I track sales by employee?', answer: 'Yes, on the plans that include employee tracking. You can record who made each sale and look at sales employee by employee.' },
      { question: 'Does Effy Order tell customers about their order?', answer: 'Yes. Order notifications can be sent to customers by WhatsApp and email.' },
    ],
    related: ['/products/effy-order/order-management-software', '/products/effy-order/quotation-software', '/products/effy-order/sales-report-software', '/products/effy-order/invoice-billing-software'],
  },
];
